import { TicketAudience } from '@21k/shared';
import { FormEvent, useEffect, useState } from 'react';

import {
  escalateCrossDept,
  escalateWithinDept,
  getApiErrorMessage,
  getTeam,
  listEscalationOptions,
  listTeams,
} from '../../api/client';
import type { DepartmentSummary, TeamDetail, TeamListItem, TicketDetail } from '../../api/types';

type Props = {
  ticket: TicketDetail;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
};

export default function EscalateModal({ ticket, open, onClose, onDone }: Props) {
  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  const [teams, setTeams] = useState<TeamListItem[]>([]);
  const [targetTeam, setTargetTeam] = useState<TeamDetail | null>(null);
  const [toTeamId, setToTeamId] = useState('');
  const [toUserId, setToUserId] = useState('');
  const [withinNote, setWithinNote] = useState('');
  const [toDepartmentId, setToDepartmentId] = useState('');
  const [crossNote, setCrossNote] = useState('');
  const [handoffParentFacing, setHandoffParentFacing] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const currentLevel = ticket.currentOwner?.team?.level ?? 0;
  const higherTeams = teams.filter((t) => t.isActive && t.level > currentLevel);
  const selectedTarget = departments.find((d) => d.id === toDepartmentId);
  const showHandoffOption =
    ticket.audience === TicketAudience.CUSTOMER &&
    selectedTarget?.canHandleParentReplies === true;

  useEffect(() => {
    if (!open) return;
    setError('');
    void listEscalationOptions(ticket.departmentId)
      .then((rows) => {
        setDepartments(rows);
        if (rows.length === 0) {
          setError('No other department is available to escalate to.');
        }
      })
      .catch((err) => setError(getApiErrorMessage(err)));
    void listTeams(ticket.departmentId).then(setTeams).catch(() => undefined);
  }, [open, ticket.departmentId]);

  useEffect(() => {
    if (!showHandoffOption) setHandoffParentFacing(false);
  }, [showHandoffOption]);

  useEffect(() => {
    if (!toTeamId) {
      setTargetTeam(null);
      return;
    }
    void getTeam(toTeamId).then(setTargetTeam).catch(() => setTargetTeam(null));
  }, [toTeamId]);

  if (!open) return null;

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError('');
    try {
      await fn();
      onDone();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleWithin(e: FormEvent) {
    e.preventDefault();
    if (!toTeamId) return;
    await run(() =>
      escalateWithinDept(ticket.id, {
        toTeamId,
        toUserId: toUserId || undefined,
        escalationNote: withinNote.trim() || undefined,
      }),
    );
  }

  async function handleCross(e: FormEvent) {
    e.preventDefault();
    if (!toDepartmentId || !crossNote.trim()) return;
    await run(() =>
      escalateCrossDept(ticket.id, {
        toDepartmentId,
        escalationNote: crossNote.trim(),
        handoffParentFacing: handoffParentFacing || undefined,
      }),
    );
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal-panel"
        role="dialog"
        aria-labelledby="escalate-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="escalate-title">Escalate ticket</h2>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
        {error && <div className="alert alert-error">{error}</div>}
        <div className="modal-body escalate-forms">
          <form onSubmit={handleWithin}>
            <h3>Within department</h3>
            <div className="form-group">
              <label>Target team</label>
              <select
                className="form-control"
                value={toTeamId}
                onChange={(e) => setToTeamId(e.target.value)}
              >
                <option value="">Select team…</option>
                {higherTeams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} (L{t.level})
                  </option>
                ))}
              </select>
            </div>
            {targetTeam && targetTeam.members.length > 0 && (
              <div className="form-group">
                <label>Assign to (optional)</label>
                <select
                  className="form-control"
                  value={toUserId}
                  onChange={(e) => setToUserId(e.target.value)}
                >
                  <option value="">Team queue</option>
                  {targetTeam.members.map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.user.firstName} {m.user.lastName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-group">
              <label>Note</label>
              <textarea
                className="form-control"
                rows={2}
                value={withinNote}
                onChange={(e) => setWithinNote(e.target.value)}
              />
            </div>
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !toTeamId}>
              Escalate within dept
            </button>
          </form>

          <form onSubmit={handleCross}>
            <h3>Cross department</h3>
            <div className="form-group">
              <label>Target department</label>
              <select
                className="form-control"
                value={toDepartmentId}
                onChange={(e) => setToDepartmentId(e.target.value)}
              >
                <option value="">Select department…</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            {showHandoffOption && (
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={handoffParentFacing}
                    onChange={(e) => setHandoffParentFacing(e.target.checked)}
                  />{' '}
                  Hand off parent-facing replies to {selectedTarget?.name}
                </label>
              </div>
            )}
            <div className="form-group">
              <label>Escalation note (required)</label>
              <textarea
                className="form-control"
                rows={2}
                value={crossNote}
                onChange={(e) => setCrossNote(e.target.value)}
                required
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={busy || !toDepartmentId || !crossNote.trim()}
            >
              Escalate cross dept
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
